import apiClient from '../apiService';
import { getStoredRefreshToken } from '../../utils/authSession';

const getRefreshToken = () => getStoredRefreshToken();

export const authAPI = {
  login: (credentials) => apiClient.post('/admin/auth/login', credentials, {
    skipAuthHeader: true,
    skipAuthRefresh: true,
    skipIdleCheck: true
  }),
  verifyLoginEmail: (payload) => apiClient.post('/admin/auth/login/verify-email', payload, {
    skipAuthHeader: true,
    skipAuthRefresh: true,
    skipIdleCheck: true
  }),
  resendLoginEmailVerification: (payload) => apiClient.post('/admin/auth/login/resend-email-verification', payload, {
    skipAuthHeader: true,
    skipAuthRefresh: true,
    skipIdleCheck: true
  }),
  refreshToken: (refreshToken = getRefreshToken()) => apiClient.post('/admin/auth/refresh-token', { refreshToken }, {
    skipAuthHeader: true,
    skipAuthRefresh: true
  }),
  logout: (refreshToken = getRefreshToken()) => apiClient.post('/admin/auth/logout', { refreshToken }, {
    skipAuthHeader: true,
    skipAuthRefresh: true,
    skipIdleCheck: true
  }),
  getProfile: () => apiClient.get('/admin/auth/profile'),
  changePassword: (payload) => apiClient.post('/admin/auth/change-password', payload)
};

export const adminAPI = {
  getOverview: () => apiClient.get('/admin/dashboard/overview'),
  getMonitoring: (params = {}) => apiClient.get('/admin/monitoring', { params }),
  getEmployers: (params = {}) => apiClient.get('/admin/employers', { params }),
  getCandidates: (params = {}) => apiClient.get('/admin/candidates', { params }),
  updateCandidateAccountStatus: (candidateId, payload) => apiClient.put(`/admin/candidates/${candidateId}/account-status`, payload),
  getJobs: (params = {}) => apiClient.get('/admin/jobs', { params }),
  updateAdminJobStatus: (jobId, payload) => apiClient.put(`/admin/jobs/${jobId}/status`, payload),
  triggerAdminJobMatching: (jobId, payload = {}) => apiClient.post(`/admin/jobs/${jobId}/trigger-matching`, payload),
  getAdminInterviews: (params = {}) => apiClient.get('/admin/interviews', { params }),
  getAdminInterview: (interviewId) => apiClient.get(`/admin/interviews/${interviewId}`),
  getAdminAssessments: (params = {}) => apiClient.get('/admin/assessments', { params }),
  getAdminAssessment: (assessmentId) => apiClient.get(`/admin/assessments/${assessmentId}`),
  getAdminOffers: (params = {}) => apiClient.get('/admin/offers', { params }),
  getAdminOffer: (offerId) => apiClient.get(`/admin/offers/${offerId}`),
  getJobInsights: (params = {}) => apiClient.get('/admin/job-insights', { params }),
  getJobInsightDetail: (jobId) => apiClient.get(`/admin/job-insights/${jobId}`),
  getConfigurations: () => apiClient.get('/admin/configurations'),
  updateGlobalMatchingPolicy: (payload) => apiClient.put('/admin/configurations/matching/global', payload),
  restoreGlobalMatchingPolicy: (configId, payload) => apiClient.post(`/admin/configurations/matching/global/${configId}/restore`, payload),
  getLegalDocuments: (params = {}) => apiClient.get('/admin/legal-documents', { params }),
  getLegalDocument: (documentId) => apiClient.get(`/admin/legal-documents/${documentId}`),
  createLegalDocument: (payload) => apiClient.post('/admin/legal-documents', payload),
  updateLegalDocument: (documentId, payload) => apiClient.put(`/admin/legal-documents/${documentId}`, payload),
  deleteLegalDocument: (documentId) => apiClient.delete(`/admin/legal-documents/${documentId}`),
  getLegalDocumentRevisions: (documentId) => apiClient.get(`/admin/legal-documents/${documentId}/revisions`),
  getTaxonomyOverview: () => apiClient.get('/admin/taxonomy/overview'),
  getTaxonomyCategories: () => apiClient.get('/admin/taxonomy/categories'),
  getCanonicalTags: (params = {}) => apiClient.get('/admin/taxonomy/canonical-tags', { params }),
  getCanonicalTag: (tagId) => apiClient.get(`/admin/taxonomy/canonical-tags/${tagId}`),
  addCanonicalTagAlias: (tagId, payload) => apiClient.post(`/admin/taxonomy/canonical-tags/${tagId}/aliases`, payload),
  deactivateCanonicalTagAlias: (tagId, aliasId) => apiClient.delete(`/admin/taxonomy/canonical-tags/${tagId}/aliases/${aliasId}`),
  getSuggestedTags: (params = {}) => apiClient.get('/admin/taxonomy/suggested-tags', { params }),
  reviewSuggestedTag: (suggestedTagId, payload) => apiClient.post(`/admin/taxonomy/suggested-tags/${suggestedTagId}/review`, payload),
  autoPromoteSuggestedTags: (payload = {}) => apiClient.post('/admin/taxonomy/suggested-tags/auto-promote', payload),
  getAdminPermissions: () => apiClient.get('/admin/admins/permissions'),
  getAdminRoles: () => apiClient.get('/admin/admins/roles'),
  createAdminRole: (payload) => apiClient.post('/admin/admins/roles', payload),
  updateAdminRole: (roleId, payload) => apiClient.put(`/admin/admins/roles/${roleId}`, payload),
  deleteAdminRole: (roleId) => apiClient.delete(`/admin/admins/roles/${roleId}`),
  getAdminUsers: () => apiClient.get('/admin/admins/users'),
  inviteAdminUser: (payload) => apiClient.post('/admin/admins/users', payload),
  updateAdminUser: (userId, payload) => apiClient.put(`/admin/admins/users/${userId}`, payload),
  getQuestionBanks: (params = {}) => apiClient.get('/admin/question-banks', { params }),
  createQuestionBankQuestion: (bank, payload) => apiClient.post(`/admin/question-banks/${bank}/questions`, payload),
  updateQuestionBankQuestion: (bank, questionId, payload) => apiClient.put(`/admin/question-banks/${bank}/questions/${questionId}`, payload),
  getQuestionBankHistory: (bank, questionId) => apiClient.get(`/admin/question-banks/${bank}/questions/${questionId}/history`),
  getNotifications: (params = {}) => apiClient.get('/admin/notifications', { params }),
  searchNotificationRecipients: (params = {}) => apiClient.get('/admin/notifications/recipients', { params }),
  createNotificationCampaign: (payload) => apiClient.post('/admin/notifications/campaigns', payload),
  getNotificationCampaign: (campaignId, params = {}) => apiClient.get(`/admin/notifications/campaigns/${campaignId}`, { params }),
  getWhatsappSenderStatus: () => apiClient.get('/admin/whatsapp/status'),
  connectWhatsappSender: () => apiClient.post('/admin/whatsapp/connect'),
  disconnectWhatsappSender: () => apiClient.post('/admin/whatsapp/disconnect'),
  getSupportTickets: (params = {}) => apiClient.get('/admin/tickets', { params }),
  getSupportTicket: (ticketId) => apiClient.get(`/admin/tickets/${ticketId}`),
  getSupportTicketAssignees: () => apiClient.get('/admin/tickets/assignees'),
  createSupportTicket: (payload) => apiClient.post('/admin/tickets', payload),
  updateSupportTicket: (ticketId, payload) => apiClient.put(`/admin/tickets/${ticketId}`, payload),
  addSupportTicketUpdate: (ticketId, payload) => apiClient.post(`/admin/tickets/${ticketId}/updates`, payload),
  downloadSupportTicketAttachment: (ticketId, attachmentId) => apiClient.get(`/admin/tickets/${ticketId}/attachments/${attachmentId}/download`, {
    responseType: 'blob'
  }),
  getBilling: (params = {}) => apiClient.get('/admin/billing', { params }),
  getCandidateBillingDetail: (userId) => apiClient.get(`/admin/billing/candidates/${userId}`),
  extendCandidateBillingAccess: (payload) => apiClient.post('/admin/billing/grants', payload),
  updateBillingPlanPrice: (planId, payload) => apiClient.post(`/admin/billing/plans/${planId}/price`, payload),
  createBillingOffer: (payload) => apiClient.post('/admin/billing/offers', payload),
  updateBillingOfferStatus: (offerId, payload) => apiClient.put(`/admin/billing/offers/${offerId}/status`, payload),
  getAdminLogs: (domain = 'overview', params = {}) => {
    const segment = domain === 'overview' ? '' : `/${domain}`;
    return apiClient.get(`/admin/logs${segment}`, { params });
  },
  getAdminLogDetail: (domain = 'overview', source, logId) => apiClient.get(`/admin/logs/${domain}/${source}/${logId}`),
  getEmployerLogs: (params = {}) => apiClient.get('/admin/logs/employers', { params }),
  getEmployerLogDetail: (source, logId) => apiClient.get(`/admin/logs/employers/${source}/${logId}`),
  getCandidateLogs: (params = {}) => apiClient.get('/admin/logs/candidates', { params }),
  getCandidateLogDetail: (source, logId) => apiClient.get(`/admin/logs/candidates/${source}/${logId}`),
  getVerificationQueue: (params = {}) => apiClient.get('/admin/verification/queue', { params }),
  getRegionalKycPolicies: () => apiClient.get('/admin/verification/config/regions'),
  getRegionalKycPolicyHistory: (regionId) => apiClient.get(`/admin/verification/config/regions/${regionId}/history`),
  createRegionalKycRequirement: (regionId, payload) => apiClient.post(`/admin/verification/config/regions/${regionId}/requirements`, payload),
  updateRegionalKycRequirement: (requirementId, payload) => apiClient.put(`/admin/verification/config/requirements/${requirementId}`, payload),
  getEmployerVerificationDetails: (employerId) => apiClient.get(`/admin/verification/${employerId}`),
  assignVerification: (employerId) => apiClient.post(`/admin/verification/${employerId}/assign`),
  updateVerificationStatus: (employerId, payload) => apiClient.put(`/admin/verification/${employerId}/status`, payload),
  updateVerificationDocument: (documentId, payload) => apiClient.put(`/admin/verification/documents/${documentId}/status`, payload),
  previewVerificationDocument: (documentId) => apiClient.get(`/admin/verification/documents/${documentId}/preview`, {
    responseType: 'blob'
  }),
  downloadVerificationDocument: (documentId) => apiClient.get(`/admin/verification/documents/${documentId}/download`, {
    responseType: 'blob'
  }),
  updateEmployerAccountStatus: (employerId, payload) => apiClient.put(`/admin/verification/${employerId}/account-status`, payload)
};

export { apiClient };
export default apiClient;
